import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { StatWidget } from '../components/custom/StatWidget';
import { PlayerCard } from '../components/custom/PlayerCard';
import { NeedCard } from '../components/custom/NeedCard';
import { statsAPI, playersAPI, needsAPI } from '../lib/api';
import { Users, Target, Building2, UserPlus, ArrowRight, Loader2, Heart } from 'lucide-react';
import { Button } from '../components/ui/button';

export const DashboardPage = () => {
  const { club } = useAuth();
  const [stats, setStats] = useState(null);
  const [recentPlayers, setRecentPlayers] = useState([]);
  const [recentNeeds, setRecentNeeds] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [statsRes, playersRes, needsRes] = await Promise.all([
        statsAPI.get(),
        playersAPI.getAll(),
        needsAPI.getAll(),
      ]);
      setStats(statsRes.data);
      setRecentPlayers(playersRes.data.slice(0, 4));
      setRecentNeeds(needsRes.data.slice(0, 3));
    } catch (error) {
      console.error('Error loading dashboard data:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="flex items-center justify-center h-64">
          <Loader2 className="w-8 h-8 animate-spin text-indigo-600" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="space-y-8 animate-fade-in" data-testid="dashboard-content">
        {/* Welcome section */}
        <div className="bg-gradient-to-r from-indigo-600 to-violet-600 rounded-xl p-6 md:p-8 text-white">
          <h1 className="font-heading text-2xl md:text-3xl font-bold uppercase tracking-tight">
            Bienvenido, {club?.name}
          </h1>
          <p className="mt-2 text-indigo-100 max-w-xl">
            Gestiona tus jugadoras disponibles y encuentra las incorporaciones que necesita tu equipo.
          </p>
          <div className="mt-6 flex flex-wrap gap-3">
            <Link to="/add-player">
              <Button className="bg-white text-indigo-600 hover:bg-indigo-50" data-testid="dash-add-player">
                <UserPlus className="w-4 h-4 mr-2" />
                Publicar Jugadora
              </Button>
            </Link>
            <Link to="/add-need">
              <Button variant="outline" className="border-white/30 text-white hover:bg-white/10" data-testid="dash-add-need">
                <Target className="w-4 h-4 mr-2" />
                Publicar Necesidad
              </Button>
            </Link>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
          <StatWidget
            title="Mis Jugadoras"
            value={stats?.my_players || 0}
            icon={Users}
            color="indigo"
          />
          <StatWidget
            title="Mis Necesidades"
            value={stats?.my_needs || 0}
            icon={Target}
            color="amber"
          />
          <StatWidget
            title="Clubes en la Red"
            value={stats?.total_clubs || 0}
            icon={Building2}
            color="lime"
          />
          <StatWidget
            title="Mis Intereses"
            value={stats?.my_interests || 0}
            icon={Heart}
            color="red"
          />
        </div>

        {/* Recent Players */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-slate-800 uppercase tracking-tight">
              Mis Jugadoras Publicadas
            </h2>
            <Link to="/players" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentPlayers.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
              {recentPlayers.map((player) => (
                <PlayerCard key={player.id} player={player} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Users className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay jugadoras publicadas aún</p>
              <Link to="/add-player">
                <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                  Sé el primero en publicar
                </Button>
              </Link>
            </div>
          )}
        </div>

        {/* Recent Needs */}
        <div>
          <div className="flex items-center justify-between mb-4">
            <h2 className="font-heading text-xl font-bold text-slate-800 uppercase tracking-tight">
              Mis Necesidades Publicadas
            </h2>
            <Link to="/needs" className="text-indigo-600 hover:text-indigo-700 text-sm font-medium flex items-center gap-1">
              Ver todas <ArrowRight className="w-4 h-4" />
            </Link>
          </div>
          
          {recentNeeds.length > 0 ? (
            <div className="space-y-4">
              {recentNeeds.map((need) => (
                <NeedCard key={need.id} need={need} />
              ))}
            </div>
          ) : (
            <div className="bg-white rounded-xl border border-slate-200 p-8 text-center">
              <Target className="w-12 h-12 text-slate-300 mx-auto mb-3" />
              <p className="text-slate-500">No hay necesidades publicadas aún</p>
              <Link to="/add-need">
                <Button className="mt-4 bg-indigo-600 hover:bg-indigo-700">
                  Publica la primera necesidad
                </Button>
              </Link>
            </div>
          )}
        </div>
      </div>
    </DashboardLayout>
  );
};

export default DashboardPage;
